import { useEffect, useRef, useState } from 'react'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { Input, NumberInput } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFormContext } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import type { LocationNodes } from 'modules/locations/hooks/useLocations'
import type { WorkAreaNodes } from 'modules/locations/hooks/useWorkAreas'

interface filterFormFields {
  startTime: string
  endTime: string
  locationId: string
  workAreaId: string
  date: string
  capacity: string
  searchQuery: string
}

interface Props {
  locations: LocationNodes
  workAreas: WorkAreaNodes
}

const DEBOUNCE_MS = 300
const MAX_CAPACITY = 9999 // Maximum reasonable workspace capacity

const WorkSpaceFilter = ({ locations, workAreas }: Props) => {
  const { access } = useCurrentSession()

  const filterForm = useFormContext<filterFormFields>()
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const scheduleSubmit = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      if (!isValidStartTime() || !isValidEndTime()) return

      const location = locations.find(
        (loc) => loc.id.toString() === filterForm.values.locationId
      )?.name
      const workArea = workAreas.find(
        (wa) => wa.id.toString() === filterForm.values.workAreaId
      )?.name
      track(trackingEvents.spaceControl.bookings.filterAvailability, {
        day: filterForm.values.date,
        startTime: filterForm.values.startTime === '' ? null : filterForm.values.startTime,
        endTime: filterForm.values.endTime === '' ? null : filterForm.values.endTime,
        location: location ?? null,
        workArea: workArea ?? null,
      })
      filterForm.handleSubmit()
    }, DEBOUNCE_MS)
  }

  useEffect(() => {
    if (filterForm.values.locationId) {
      const locationId = filterForm.values.locationId
      if (locationId && locationId !== selectedLocation) {
        setSelectedLocation(locationId)
      }
    } else {
      setSelectedLocation(null)
    }
  }, [filterForm.values.locationId])

  useEffect(() => {
    if (selectedLocation !== null && filterForm.values.workAreaId) {
      const currentWorkArea = workAreas.find(
        (wa) => wa.id.toString() === filterForm.values.workAreaId
      )
      const workAreaBelongsToLocation = currentWorkArea?.location?.id === selectedLocation

      if (!workAreaBelongsToLocation) {
        filterForm.setFieldValue('workAreaId', '')
      }
    }
  }, [selectedLocation, filterForm.values.workAreaId, workAreas])

  const locationsOptions = locations.map((location) => ({
    label: location.name,
    value: location.id,
  }))

  const workAreasOptions = workAreas
    .map((workArea) => ({
      label: `${workArea.location?.name} - ${workArea.name}`,
      value: workArea.id,
      locationId: workArea.location?.id,
    }))
    .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label))

  const now = moment()

  const isValidStartTime = () => {
    if (filterForm.errors.startTime) {
      return false
    }

    if (
      !filterForm.values.startTime ||
      moment(filterForm.values.date).isAfter(moment().startOf('day'))
    ) {
      return true
    }

    const splitTime = filterForm.values.startTime.split(':')
    if (splitTime.length === 2) {
      const selectedHour = splitTime[0] ? parseInt(splitTime[0]) : now.hour()
      const selectedMinute = splitTime[1] ? parseInt(splitTime[1]) : now.minute()
      const currentHour = now.hour()
      const currentMinute = now.minute()

      return (
        selectedHour > currentHour ||
        (selectedHour === currentHour && selectedMinute >= currentMinute)
      )
    }
    return false
  }

  const isValidEndTime = () => {
    if (filterForm.errors.endTime) {
      return false
    }
    if (!filterForm.values.endTime) {
      return true
    }

    const splitTime = filterForm.values.endTime.split(':')
    const splitStartTime = filterForm.values.startTime.split(':')
    if (splitTime.length === 2 && splitStartTime.length === 2) {
      const selectedHour = splitTime[0] ? parseInt(splitTime[0]) : now.hour()
      const selectedMinute = splitTime[1] ? parseInt(splitTime[1]) : now.minute()
      const startTimeHour = splitStartTime[0] ? parseInt(splitStartTime[0]) : now.hour()
      const startTimeMinute = splitStartTime[1] ? parseInt(splitStartTime[1]) : now.minute()

      return (
        selectedHour > startTimeHour ||
        (selectedHour === startTimeHour && selectedMinute >= startTimeMinute)
      )
    }
    return false
  }

  return (
    <Box
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
    >
      <DayPicker
        minDate={moment().startOf('day')}
        value={filterForm.values.date}
        onChange={(value) => {
          const formatted = stringToDate(value, 'ui')
          filterForm.setFieldValue('date', formatted)
          scheduleSubmit()
        }}
        mandatory
        showOnlyCalendar
        firstDayOfWeek={firstDayOfWeekAsIndex(access.firstDayOfWeek)}
      />
      <Box gap="s16" padding="s24">
        <Divider />
        <Input
          label={i18n.t('space_control.overview.filters.search_placeholder')}
          placeholder={i18n.t('space_control.overview.filters.search_placeholder')}
          value={filterForm.values.searchQuery}
          onChange={(value) => {
            filterForm.setFieldValue('searchQuery', value)
            scheduleSubmit()
          }}
          size="md"
        />
        <Box flexDirection="row" gap="s16">
          <Input
            type="time"
            label={i18n.t('space_control.overview.filters.start_time')}
            value={filterForm.values.startTime}
            onChange={(value) => {
              filterForm.setValues({ ...filterForm.values, startTime: value })
              scheduleSubmit()
            }}
            error={!isValidStartTime() ? filterForm.errors.startTime : undefined}
            size="md"
          />
          <Input
            type="time"
            label={i18n.t('space_control.overview.filters.end_time')}
            value={filterForm.values.endTime}
            onChange={(value) => {
              filterForm.setValues({ ...filterForm.values, endTime: value })
              scheduleSubmit()
            }}
            error={!isValidEndTime() ? filterForm.errors.endTime : undefined}
            size="md"
          />
        </Box>

        <F0Select
          placeholder={i18n.t('space_control.overview.filters.all_workplaces')}
          label={i18n.t('space_control.overview.filters.location')}
          options={locationsOptions.map((loc) => ({
            label: loc.label,
            value: loc.value.toString(),
          }))}
          value={filterForm.values.locationId}
          onChange={(locationId: string) => {
            setSelectedLocation(locationId)
            filterForm.setFieldValue('locationId', locationId)
            scheduleSubmit()
          }}
          size="md"
          defaultItem={{
            label: i18n.t('space_control.overview.filters.all_workplaces'),
            value: '',
          }}
          searchable
          showSearchBox
        />
        <F0Select
          placeholder={i18n.t('space_control.overview.filters.all_workareas')}
          label={i18n.t('space_control.overview.filters.workarea')}
          options={
            selectedLocation
              ? workAreasOptions
                  .filter((workArea) => workArea.locationId === selectedLocation)
                  .map((wa) => ({ label: wa.label, value: wa.value.toString() }))
              : workAreasOptions.map((wa) => ({ label: wa.label, value: wa.value.toString() }))
          }
          value={filterForm.values.workAreaId}
          onChange={(workAreaId: string) => {
            filterForm.setFieldValue('workAreaId', workAreaId)
            scheduleSubmit()
          }}
          size="md"
          defaultItem={{ label: i18n.t('space_control.overview.filters.all_workareas'), value: '' }}
          searchable
          showSearchBox
        />
        <NumberInput
          label={i18n.t('space_control.overview.filters.capacity')}
          placeholder={i18n.t('space_control.overview.filters.any_capacity')}
          value={filterForm.values.capacity ? Number(filterForm.values.capacity) : null}
          onChange={(value) => {
            if (value === null) {
              filterForm.setFieldValue('capacity', '')
              scheduleSubmit()
            } else {
              const numValue = Number(value)
              if (!isNaN(numValue) && numValue > 0 && numValue <= MAX_CAPACITY) {
                filterForm.setFieldValue('capacity', numValue.toString())
                scheduleSubmit()
              }
            }
          }}
          size="md"
          locale="en-US"
        />
      </Box>
    </Box>
  )
}

export default observer(WorkSpaceFilter)
